import { lazy, Suspense, useEffect } from 'react'
import { Provider, useDispatch } from 'react-redux'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import Layout from './components/Layout/Layout'
import ErrorBoundary from './components/ui/ErrorBoundary/ErrorBoundary'
import RootErrorFallback from './components/ui/ErrorFallback/RootErrorFallback'
import RouteFallback from './components/ui/RouteFallback/RouteFallback'
import { AuthProvider } from './contexts/AuthContext'
import { ConsentProvider } from './analytics'
import { usePageTracking } from './hooks/usePageTracking'
import HomePage from './pages/HomePage/HomePage'
import NotFoundPage from './pages/NotFoundPage/NotFoundPage'
import ProductPage from './pages/ProductPage/ProductPage'
import ShopPage from './pages/ShopPage/ShopPage'
import { fetchCategories } from './store/slices/categoriesSlice'
import { AppDispatch, store } from './store/store'
import { usePaymentReturn } from './hooks/usePaymentReturn'

const CheckoutPage = lazy(() => import('./pages/CheckoutPage/CheckoutPage'))
const ContactsPage = lazy(() => import('./pages/ContactsPage/ContactsPage'))
const DeliveryPaymentPage = lazy(() => import('./pages/DeliveryPaymentPage/DeliveryPaymentPage'))
const ExchangePage = lazy(() => import('./pages/ExchangePage/ExchangePage'))
const MapsPage = lazy(() =>
  import('./pages/MapsPage/MapsPage').then(module => ({ default: module.MapsPage })),
)
const VacanciesPage = lazy(() =>
  import('./pages/VacanciesPage/VacanciesPage').then(module => ({ default: module.VacanciesPage })),
)
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage/PrivacyPolicyPage'))
const PublicOfferPage = lazy(() => import('./pages/PublicOfferPage/PublicOfferPage'))
const AppPublicOfferPage = lazy(() => import('./pages/AppLegalPages/AppPublicOfferPage'))
const AppPrivacyPolicyPage = lazy(() => import('./pages/AppLegalPages/AppPrivacyPolicyPage'))
const OrderSuccesWrapper = lazy(() =>
  import('./pages/SuccessPage/OrderSuccesWrapper').then(module => ({
    default: module.OrderSuccesWrapper,
  })),
)
const AccountLayout = lazy(() => import('./components/Layout/AccountLayout/AccountLayout'))
const AddressesPage = lazy(() => import('./pages/AddressesPage/AddressesPage'))
const OrdersPage = lazy(() => import('./pages/OrdersPage/OrdersPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'))
const SupportPage = lazy(() => import('./pages/SupportPage/SupportPage'))
const WishlistPage = lazy(() => import('./pages/WishlistPage/WishlistPage'))

const AppInitializer = () => {
  const dispatch = useDispatch<AppDispatch>()

  usePaymentReturn()
  usePageTracking()

  useEffect(() => {
    void import(/* webpackChunkName: "cognito" */ './services/cognito').then(module => {
      module.initializeCognito()
    })
    dispatch(fetchCategories({ limit: 20 }))
  }, [dispatch])

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/shop/" element={<ShopPage />} />
        <Route path="/shop/tag/:tag" element={<ShopPage />} />
        <Route path="/shop/:category" element={<ShopPage />} />
        <Route path="/product/:slug" element={<ProductPage />} />

        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/vacancies" element={<VacanciesPage />} />
        <Route path="/maps" element={<MapsPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/delivery-payment" element={<DeliveryPaymentPage />} />
        <Route path="/exchange" element={<ExchangePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/public-offer" element={<PublicOfferPage />} />
        <Route path="/app/public-offer" element={<AppPublicOfferPage />} />
        <Route path="/app/privacy-policy" element={<AppPrivacyPolicyPage />} />
        <Route path="/checkout-success/:orderNumber" element={<OrderSuccesWrapper />} />
        <Route path="/account" element={<AccountLayout />}>
          <Route path="orders" element={<OrdersPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="addresses" element={<AddressesPage />} />
          <Route path="wishlist" element={<WishlistPage />} />
          <Route path="support" element={<SupportPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <ErrorBoundary fallback={<RootErrorFallback />}>
      <Provider store={store}>
        <ConsentProvider>
          <AuthProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <Layout>
                <ErrorBoundary>
                  <AppInitializer />
                </ErrorBoundary>
              </Layout>
            </Router>
          </AuthProvider>
        </ConsentProvider>
      </Provider>
    </ErrorBoundary>
  )
}
